import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { SIGNED_IN_CAM } from '@/cam-admin/care-circles/fixtures';
import {
  COVER_PERIODS,
  coverDuration,
  formatCoverRange,
  type CoverPeriod,
  type CoverStatus,
} from '@/cam-admin/care-circles/LeaveCover.fixtures';

// Which side of a period you are on. 'mine' is leave you have booked, so the
// third column is who is holding your circles; 'covering' is somebody else's
// leave that you are the cover for, so it is whose circles you are holding.
// Same rows, read from opposite ends.
export type CoverDirection = 'mine' | 'covering';

const COLUMNS: Record<CoverDirection, string[]> = {
  mine: ['Period', 'Duration', 'Cover', 'Status'],
  covering: ['Period', 'Duration', 'Covering for', 'Status'],
};

// Active now first: it is the one with consequences today. Past sinks.
const ORDER: CoverStatus[] = ['active', 'upcoming', 'past'];

const STATUS_LABEL: Record<CoverStatus, string> = {
  active: 'Active now',
  upcoming: 'Upcoming',
  past: 'Past',
};

const statusColor = (status: CoverStatus): 'limeGreen' | 'default' =>
  status === 'active' ? 'limeGreen' : 'default';

const byStatusThenStart = (a: CoverPeriod, b: CoverPeriod) =>
  ORDER.indexOf(a.status) - ORDER.indexOf(b.status) ||
  a.start.localeCompare(b.start);

/**
 * The team's leave as a table, one row per period, opening into a drawer.
 *
 * It was a list of expandable cards, each carrying the handover note and the
 * per-circle cover table inside it. That put the whole of every period on the
 * page at once and still made you expand each one to compare them, when the
 * question the page answers is which dates are covered and by whom. A table
 * answers that in a glance, and the row opens the rest.
 *
 * Presentational: the actions are mocked in the prototype.
 */
const LeaveCover = ({
  periods = COVER_PERIODS,
  direction = 'mine',
  onScheduleCover,
  onOpen,
}: {
  periods?: CoverPeriod[];
  direction?: CoverDirection;
  onScheduleCover?: () => void;
  /** A row was clicked — the caller opens it in the cover drawer. */
  onOpen?: (id: string) => void;
}) => {
  const mine = direction === 'mine';
  const rows = periods
    .filter((p) =>
      mine ? p.owner === SIGNED_IN_CAM : p.mainCover === SIGNED_IN_CAM,
    )
    .sort(byStatusThenStart);

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
        }}
      >
        <Typography sx={{ mb: 1 }} variant='h3' color='primary.main'>
          {mine ? 'No cover scheduled' : 'You’re not covering anyone'}
        </Typography>
        <Typography sx={{ mb: 3 }} variant='body1' color='text.secondary'>
          {mine
            ? 'When you’re going to be away, schedule cover so a colleague picks up your circles while you’re off.'
            : 'When a colleague books leave and names you as their cover, their circles show up here.'}
        </Typography>
        {mine && (
          <Button
            variant='primary'
            size='md'
            ariaLabel='Schedule cover'
            onPress={() => onScheduleCover?.()}
          >
            Schedule cover
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1, pt: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            {COLUMNS[direction].map((col) => (
              <TableCell key={col}>
                <Typography variant='body2' color='text.secondary'>
                  {col}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((period) => (
            <TableRow
              key={period.id}
              hover
              onClick={() => onOpen?.(period.id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                {formatCoverRange(period.start, period.end)}
              </TableCell>
              <TableCell>{coverDuration(period.start, period.end)}</TableCell>
              <TableCell>{mine ? period.mainCover : period.owner}</TableCell>
              <TableCell>
                <Chip
                  label={STATUS_LABEL[period.status]}
                  size='small'
                  color={statusColor(period.status)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Booking your own leave belongs under your own list, not under the one
          of somebody else's you are covering. */}
      {mine && (
        <Stack sx={{ alignItems: 'flex-start', pt: 3 }}>
          <Button
            variant='secondary'
            size='md'
            ariaLabel='Schedule cover'
            onPress={() => onScheduleCover?.()}
          >
            Schedule cover
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default LeaveCover;
